import { useEffect, useRef, useState } from 'react';
import { api, formatApiError, reportPdfUrl } from '@/lib/api';
import { toast } from 'sonner';
import { useOnline } from '@/lib/useOnline';
import { Spinner } from '@/components/Common';
import { TEST_IDS } from '@/constants/ids';
import UpsellCards from '@/pages/engagement/UpsellCards';
import PreDeliveryModal from '@/pages/engagement/PreDeliveryModal';
import { ScoreCard, StakesCard, RoadmapCard, EmailCard, InternalCard } from '@/pages/engagement/DossierHero';
import { SendPill } from '@/components/v3/kit';
import {
  FileTextIcon,
  CopyIcon,
  EnvelopeSimpleIcon,
  CheckCircleIcon,
  EyeSlashIcon,
  EyeIcon,
  DownloadSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';

export default function ReportTab({ eng, refresh, onJumpTab }) {
  const online = useOnline();
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [roadmapKey, setRoadmapKey] = useState(0);

  async function load() {
    try {
      const { data } = await api.get(`/engagements/${eng.id}/reports`);
      setReports(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setReports([]);
    }
  }
  useEffect(() => {
    load();
  }, [eng.id]);

  async function generate() {
    if (!online) {
      toast.error('Dossier generation needs connection.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      await api.post(`/engagements/${eng.id}/report`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Dossier generated');
      await load();
      setRoadmapKey((k) => k + 1);
      refresh && refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (reports === null) return <Spinner label="Loading dossier…" />;

  const active = reports.find((r) => r.is_active);
  const score = active?.compliance_score ?? null;

  async function sendToClient() {
    if (!active) return;
    try {
      const fd = new FormData();
      const { data } = await api.post(`/reports/${active.id}/send_email`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const bccNote = data?.bcc?.length ? ` (BCC: ${data.bcc.join(', ')})` : '';
      if (eng.status !== 'delivered') {
        await api.patch(`/engagements/${eng.id}/mark_delivered`).catch(() => {});
      }
      toast.success(`Dossier sent to ${eng.contact_email}${bccNote}`);
      setCheckOpen(false);
      await load();
      refresh && refresh();
    } catch (e) {
      const msg = formatApiError(e);
      if (msg.toLowerCase().includes('resend_api_key') || msg.toLowerCase().includes('not configured')) {
        toast.error('Email is not configured yet — opening your mail app instead.');
        emailHandoff(eng, active);
      } else {
        toast.error(msg);
      }
    }
  }

  return (
    <div className="max-w-5xl" data-testid="dossier-tab">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, marginBottom: 18 }}>
        <ScoreCard
          report={active}
          score={score}
          busy={busy}
          online={online}
          onPreview={() => active && window.open(reportPdfUrl(active.id), '_blank', 'noopener')}
          onRegenerate={generate}
        />
        <StakesCard report={active} />
      </div>

      {active && (
        <div style={{ marginBottom: 18 }}>
          <SendPill testid="send-direct-btn" onClick={() => setCheckOpen(true)}>
            {!online ? 'Needs connection' : eng.status === 'delivered' ? 'Send again' : 'Review & send to client'}
          </SendPill>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <RoadmapCard eng={eng} reloadKey={roadmapKey} />
      </div>

      {active && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, marginBottom: 18 }}>
            <EmailCard report={active} />
            <InternalCard>
              <UpsellCards eng={eng} compact />
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/engagements/${eng.id}/scope_sheet/pdf?auth=${encodeURIComponent(localStorage.getItem('gl_token') || '')}`}
                target="_blank"
                rel="noreferrer"
                className="gl-btn gl-btn-secondary !h-10 !px-4 !text-xs self-start"
                data-testid="scope-sheet-pdf-btn"
              >
                Phase 2 scope sheet (PDF)
              </a>
            </InternalCard>
          </div>
          <ActiveReport report={active} eng={eng} refresh={() => { load(); refresh && refresh(); }} />
        </>
      )}

      {reports.length > 1 && (
        <div className="mt-8">
          <div className="gl-label">Previous revisions</div>
          <div className="space-y-2">
            {reports.slice(1).map((r) => (
              <div key={r.id} className="gl-card flex items-center gap-3 flex-wrap">
                <FileTextIcon size={20} weight="bold" />
                <div className="flex-1 text-sm">
                  <div className="font-display font-extrabold uppercase tracking-wide" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.05em' }}>
                    {r.doc_no ? `${r.doc_no} · REV ${String(r.version || 1).padStart(2, '0')}` : 'Legacy report'} · {new Date(r.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs uppercase font-bold mt-0.5" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                    {r.is_active ? 'ACTIVE' : 'SUPERSEDED'}
                    {r.compliance_score != null && ` · Score ${Math.round(r.compliance_score)}%`}
                  </div>
                </div>
                <a
                  href={reportPdfUrl(r.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="gl-btn gl-btn-secondary !h-10 !px-3"
                  data-testid={`download-old-pdf-${r.id}`}
                >
                  <DownloadSimpleIcon size={18} weight="bold" />
                </a>
                <button
                  className="gl-btn gl-btn-danger !h-10 !px-3"
                  onClick={() => deleteReport(r, load)}
                  data-testid={`delete-report-${r.id}`}
                  title="Delete this report"
                >
                  <TrashIcon size={18} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {checkOpen && active && (
        <PreDeliveryModal
          eng={eng}
          onClose={() => setCheckOpen(false)}
          onSend={sendToClient}
          onFix={(tab) => onJumpTab && onJumpTab(tab)}
        />
      )}
    </div>
  );
}

async function deleteReport(r, reload) {
  if (!window.confirm('Delete this report version? The PDF and share link for this version will stop working.')) return;
  try {
    await api.delete(`/reports/${r.id}`);
    toast.success('Report deleted');
    reload();
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

function emailHandoff(eng, report) {
  const publicLink = `${window.location.origin}/r/${report.public_token}`;
  const subject = encodeURIComponent(`Your GigLine Site Safety Dossier — ${eng.business_name}`);
  const body = encodeURIComponent(
    `Your Site Safety Dossier for ${eng.business_name} is ready.\n\nView it and download the PDF: ${publicLink}\n\nQuestions — call or text (336) 329-8899.\n\n— GigLine Safety & Compliance`
  );
  window.location.href = `mailto:${eng.contact_email || ''}?subject=${subject}&body=${body}`;
}

function ActiveReport({ report, eng, refresh }) {
  const publicLink = `${window.location.origin}/r/${report.public_token}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success('Client link copied');
    } catch (e) {
      toast.error('Could not copy');
    }
  }

  async function toggleActive() {
    try {
      if (report.is_active) {
        await api.patch(`/reports/${report.id}/deactivate`);
        toast.success('Link deactivated');
      } else {
        await api.patch(`/reports/${report.id}/activate`);
        toast.success('Link reactivated');
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function markDelivered() {
    try {
      await api.patch(`/engagements/${eng.id}/mark_delivered`);
      toast.success('Marked as delivered');
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-5">
      <div className="gl-card" data-testid="dossier-link-card">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircleIcon size={20} weight="fill" color="var(--gl-p3)" />
          <div className="font-display font-extrabold uppercase tracking-widest" style={{ color: 'var(--gl-dark)' }}>
            Client share link
          </div>
          <span className="ml-auto text-xs uppercase tracking-widest" style={{ color: 'var(--gl-muted)', fontFamily: 'JetBrains Mono, monospace' }} data-testid="dossier-meta">
            {report.doc_no ? `${report.doc_no} · REV ${String(report.version || 1).padStart(2, '0')}` : `v${report.id.slice(0, 8)}`}
            {report.sheet_count ? ` · ${report.sheet_count} sheets` : ''}
          </span>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input className="gl-input flex-1" value={publicLink} readOnly data-testid="public-link-input" />
          <button className="gl-btn gl-btn-primary" onClick={copy} data-testid={TEST_IDS.copyLinkBtn}>
            <CopyIcon size={18} weight="bold" /> Copy
          </button>
          <button className="gl-btn gl-btn-secondary" onClick={() => emailHandoff(eng, report)} data-testid={TEST_IDS.emailHandoffBtn} title="Open device mail client instead">
            <EnvelopeSimpleIcon size={18} weight="bold" /> Mail App
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={reportPdfUrl(report.id)} target="_blank" rel="noreferrer" className="gl-btn gl-btn-accent" data-testid={TEST_IDS.downloadPdfBtn}>
            <DownloadSimpleIcon size={18} weight="bold" /> Download PDF
          </a>
          <button className="gl-btn gl-btn-secondary" onClick={toggleActive} data-testid={TEST_IDS.deactivateLinkBtn}>
            {report.is_active ? <EyeSlashIcon size={18} weight="bold" /> : <EyeIcon size={18} weight="bold" />}
            {report.is_active ? 'Deactivate Link' : 'Reactivate Link'}
          </button>
          {eng.status !== 'delivered' && (
            <button className="gl-btn gl-btn-primary ml-auto" onClick={markDelivered} data-testid={TEST_IDS.markDeliveredBtn}>
              <CheckCircleIcon size={18} weight="bold" /> Mark Delivered
            </button>
          )}
        </div>
      </div>

      {report.last_sent_at && <EmailAuditTrail reportId={report.id} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ExecutiveSummaryPreview text={report.executive_summary} />
        <RemediationNarrativePreview
          text={report.remediation_narrative}
          reportId={report.id}
          onRestructured={() => refresh && refresh()}
        />
      </div>

      <CoverLetterEditor report={report} onSaved={() => refresh && refresh()} />
      <AttachmentsPanel report={report} onChanged={() => refresh && refresh()} />
    </div>
  );
}

// --- Premium previews that mirror the PDF treatment ---
// Both components are deliberately read-only displays; editing happens via
// regeneration. They convert the AI prose into a designed deliverable so the
// in-app view matches what Kevin actually sees in the PDF.

function ExecutiveSummaryPreview({ text }) {
  const raw = (text || '').trim();
  if (!raw) {
    return (
      <div className="gl-card">
        <div className="gl-label">Executive Summary</div>
        <p className="text-sm text-neutral-400">—</p>
      </div>
    );
  }
  // Split on paragraph breaks first, then sentence boundaries.
  const paragraphs = raw
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  // Drop cap = first letter of the very first paragraph.
  const first = paragraphs[0] || raw;
  const dropCap = first[0] || '';
  const firstRest = first.slice(1);
  const otherParagraphs = paragraphs.slice(1);

  return (
    <div
      className="relative rounded-lg border border-neutral-200 bg-white p-6 shadow-sm overflow-hidden"
      data-testid="report-exec-summary-preview"
    >
      {/* Gold left rail */}
      <div className="absolute left-0 top-6 bottom-6 w-[3px] bg-[#C9A84C] rounded-full" aria-hidden="true" />

      <div className="text-[10px] font-semibold tracking-[0.18em] text-[#B3923F] uppercase mb-4 ml-3">
        Executive Summary
      </div>

      {/* Editorial body — display serif throughout, generous leading.
          The first letter is rendered as a drop cap so the eye lands
          there first, anchoring the block as a designed piece. */}
      <div className="ml-3">
        <p
          className="font-serif text-[15px] leading-[1.75] text-neutral-700"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          <span
            className="float-left mr-2 leading-[0.85] text-[44px] text-[#1C2B2B]"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            aria-hidden="true"
          >
            {dropCap}
          </span>
          {firstRest}
        </p>
        {otherParagraphs.map((para, i) => (
          <p
            key={i}
            className="font-serif text-[14px] leading-[1.75] text-neutral-600 mt-4"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            {para}
          </p>
        ))}
      </div>
    </div>
  );
}

function RemediationNarrativePreview({ text, reportId, onRestructured }) {
  const [busy, setBusy] = useState(false);
  const raw = (text || '').trim();
  if (!raw) {
    return (
      <div className="gl-card">
        <div className="gl-label">Remediation Narrative</div>
        <p className="text-sm text-neutral-400">—</p>
      </div>
    );
  }
  const actions = splitNarrativeIntoActions(raw);
  const looksUnstructured = actions.length < 2;

  async function restructure() {
    if (!reportId) return;
    if (
      !window.confirm(
        'Rewrite this narrative as 4-8 explicitly ordered steps?\n\n' +
          'The AI will preserve every action and CFR citation but rewrite the prose ' +
          'using "First, Second, Third, ... Finally," markers so it renders cleanly ' +
          'as an Action Sequence. Your previous narrative will be replaced.'
      )
    )
      return;
    setBusy(true);
    try {
      await api.post(`/reports/${reportId}/restructure_narrative`);
      toast.success('Narrative restructured.');
      onRestructured && onRestructured();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm"
      data-testid="report-remediation-preview"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[10px] font-bold tracking-[0.18em] text-[#C9A84C] uppercase">
          Recommended Action Sequence
        </div>
        {reportId && (
          <button
            type="button"
            onClick={restructure}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
            style={{
              background: looksUnstructured ? 'rgba(201, 168, 76, 0.18)' : '#F3F4F6',
              color: looksUnstructured ? '#8A6D1F' : '#6B7280',
              fontFamily: 'Inter',
              border: looksUnstructured ? '1px solid rgba(201, 168, 76, 0.5)' : 'none',
            }}
            title={
              looksUnstructured
                ? 'This narrative reads as one paragraph. Click to have the AI rewrite it as numbered steps.'
                : 'Rewrite this narrative as numbered "First, Second, Third..." steps.'
            }
            data-testid="report-restructure-narrative"
          >
            {busy ? 'Restructuring…' : looksUnstructured ? '✦ Restructure as Steps' : 'Re-structure'}
          </button>
        )}
      </div>
      {actions.length >= 2 ? (
        <ol className="space-y-3">
          {actions.map((action, idx) => (
            <li
              key={idx}
              className="flex gap-3 pb-3 border-b border-neutral-100 last:border-0 last:pb-0"
            >
              <span
                className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-sm bg-[#142654] text-[#C9A84C] font-bold text-sm"
                aria-hidden="true"
              >
                {idx + 1}
              </span>
              <p className="text-[13.5px] leading-[1.55] text-neutral-800">
                {action}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-[13.5px] leading-[1.65] text-neutral-700 whitespace-pre-wrap">
          {raw}
        </p>
      )}
    </div>
  );
}

// GL-SPEC-APP-022 — Resilient narrative splitter.
// Tries 3 strategies in order until one yields between 3 and 10 chunks:
//   1) explicit sequence markers (First, Second, Next, Finally, ...)
//   2) imperative-verb sentence starts (Install, Secure, Develop, Update, ...)
//   3) sentence-boundary chunking (groups N sentences into ~6 actions)
// Returns [] when nothing produces a useful split → renderer falls back to prose.
function splitNarrativeIntoActions(raw) {
  const explicitRe =
    /\b(?:First(?:ly)?,|Second(?:ly)?,|Third(?:ly)?,|Fourth(?:ly)?,|Fifth(?:ly)?,|Sixth(?:ly)?,|Seventh(?:ly)?,|Eighth(?:ly)?,|Next,|Then,|Subsequently,|Finally,|Lastly,|Additionally,|Also,)/g;
  const matches = [...raw.matchAll(explicitRe)];
  if (matches.length >= 2) {
    const out = [];
    matches.forEach((m, i) => {
      const start = m.index ?? 0;
      const end = i + 1 < matches.length ? matches[i + 1].index : raw.length;
      const chunk = raw.slice(start, end).trim().replace(/\.+$/, '') + '.';
      if (chunk.length > 20) out.push(chunk);
    });
    if ((matches[0].index ?? 0) > 30) {
      const prefix = raw.slice(0, matches[0].index).trim().replace(/\.+$/, '') + '.';
      if (prefix.length > 20) out.unshift(prefix);
    }
    // Safety net: if any chunk is > 280 chars it means the AI crammed multiple
    // actions under one marker. Sub-split such chunks on sentence boundaries
    // so the visual grid stays readable.
    const expanded = [];
    for (const chunk of out) {
      if (chunk.length <= 280) {
        expanded.push(chunk);
        continue;
      }
      const sentences = chunk
        .split(/(?<=[.!?])\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 15);
      // Keep the marker on the first sentence, render rest as their own steps.
      if (sentences.length > 1) {
        expanded.push(...sentences);
      } else {
        expanded.push(chunk);
      }
    }
    if (expanded.length >= 2 && expanded.length <= 12) return expanded;
  }

  // Strategy 2 — imperative-verb sentence starts.
  // Splits on sentence boundaries that begin with a known imperative.
  const imperativeStart =
    /(?:^|(?<=[.!?])\s+)(?=(?:Start with|Begin |Address |Develop |Establish |Conduct |Integrate |Update |Obtain |Label |Install |Secure |Complete |Train |Verify |Document |Schedule |Close out|Close the|Lock down|Replace |Remove |Repair |Implement |Build |Create |Prioritize ))/;
  const impChunks = raw
    .split(imperativeStart)
    .map((s) => s.trim())
    .filter((s) => s.length > 25);
  if (impChunks.length >= 3 && impChunks.length <= 10) return impChunks;

  // Strategy 3 — generic sentence chunking. Aim for ~6 cards; group when >6.
  const sentences = raw
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 15);
  if (sentences.length >= 3 && raw.length > 250) {
    const target = 6;
    if (sentences.length <= target) return sentences;
    const size = Math.ceil(sentences.length / target);
    const grouped = [];
    for (let i = 0; i < sentences.length; i += size) {
      grouped.push(sentences.slice(i, i + size).join(' '));
    }
    return grouped;
  }

  return [];
}


// GL-SPEC-APP-024 — Email Audit Trail panel
// Every send + Resend webhook event for a report, in reverse-chrono.
// Shows: timestamp, event type, recipient, BCC list, click URL, bounce reason.
const EVENT_META = {
  sent: { label: 'Sent', dot: '#2A52A0', tone: 'navy' },
  'email.sent': { label: 'Sent (Resend)', dot: '#2A52A0', tone: 'navy' },
  'email.delivered': { label: 'Delivered', dot: '#639922', tone: 'green' },
  'email.opened': { label: 'Opened', dot: '#C9A84C', tone: 'gold' },
  'email.clicked': { label: 'Clicked link', dot: '#C9A84C', tone: 'gold' },
  'email.bounced': { label: 'Bounced', dot: '#C0392B', tone: 'red' },
  'email.complained': { label: 'Marked spam', dot: '#C0392B', tone: 'red' },
};

function EmailAuditTrail({ reportId }) {
  const [events, setEvents] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/reports/${reportId}/email_events`);
      setEvents(data || []);
    } catch (_e) {
      setEvents([]);
    }
  }
  useEffect(() => {
    load();
    // Poll every 30s so webhook-driven events appear without refresh
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [reportId]);

  if (events === null) return null;
  if (events.length === 0) return null;

  const sentCount = events.filter((e) => e.event_type === 'sent' || e.event_type === 'email.sent').length;
  const openedCount = events.filter((e) => e.event_type === 'email.opened').length;
  const bouncedCount = events.filter((e) => e.event_type === 'email.bounced').length;

  return (
    <div
      className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200"
      data-testid="email-audit-trail"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 text-left"
        aria-expanded={!collapsed}
      >
        <div className="flex-1">
          <div className="text-[10px] font-bold tracking-[0.18em] text-[#C9A84C] uppercase mb-0.5">
            Email Audit Trail
          </div>
          <div className="text-base font-display font-extrabold tracking-tight text-[#1C2B2B]">
            {events.length} event{events.length === 1 ? '' : 's'}
            {sentCount > 0 && <span className="text-neutral-500 font-normal text-sm"> · {sentCount} send{sentCount === 1 ? '' : 's'}</span>}
            {openedCount > 0 && <span className="text-[#8A6D1F] font-normal text-sm"> · {openedCount} open{openedCount === 1 ? '' : 's'}</span>}
            {bouncedCount > 0 && <span className="text-[#C0392B] font-normal text-sm"> · {bouncedCount} bounce{bouncedCount === 1 ? '' : 's'}</span>}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.14em] font-bold text-neutral-500"
          style={{ fontFamily: 'Inter' }}
        >
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </button>

      {!collapsed && (
        <ol className="mt-4 relative">
          {/* Vertical timeline rail */}
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-neutral-200" aria-hidden="true" />
          {events.map((e) => {
            const meta = EVENT_META[e.event_type] || { label: e.event_type, dot: '#6B7280', tone: 'gray' };
            const ts = e.created_at ? new Date(e.created_at) : null;
            const clickUrl = (e.payload || {}).data?.click?.link || (e.payload || {}).click?.link;
            const bounceReason = (e.payload || {}).data?.bounce?.message || (e.payload || {}).bounce?.message;
            return (
              <li key={e.id} className="relative pl-7 pb-4 last:pb-0">
                <span
                  className="absolute left-0 top-1 w-3.5 h-3.5 rounded-full ring-4 ring-white"
                  style={{ background: meta.dot }}
                  aria-hidden="true"
                />
                <div className="text-[13px] font-bold text-[#1C2B2B]">{meta.label}</div>
                <div className="text-[11.5px] text-neutral-500 mt-0.5" style={{ fontFamily: 'Inter' }}>
                  {ts ? ts.toLocaleString() : ''}
                </div>
                {e.to && (
                  <div className="text-[12.5px] text-neutral-700 mt-1">
                    <span className="text-neutral-500">To:</span>{' '}
                    <span className="font-mono">{e.to}</span>
                  </div>
                )}
                {e.bcc && e.bcc.length > 0 && (
                  <div className="text-[12.5px] text-neutral-700">
                    <span className="text-neutral-500">BCC:</span>{' '}
                    <span className="font-mono">{e.bcc.join(', ')}</span>
                  </div>
                )}
                {clickUrl && (
                  <div className="text-[12.5px] text-neutral-700 mt-0.5 truncate">
                    <span className="text-neutral-500">Link:</span>{' '}
                    <a
                      href={clickUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[#2A52A0] hover:underline"
                    >
                      {clickUrl}
                    </a>
                  </div>
                )}
                {bounceReason && (
                  <div className="text-[12.5px] text-[#C0392B] mt-0.5">{bounceReason}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// GL-SPEC-APP-025 — Personalized Cover Letter editor.
// Renders as Page 2 of the next-generated PDF deliverable.
function CoverLetterEditor({ report, onSaved }) {
  const [draft, setDraft] = useState(report.cover_letter || '');
  const [busy, setBusy] = useState(false);
  const dirty = draft !== (report.cover_letter || '');

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/reports/${report.id}/cover_letter`, { cover_letter: draft });
      toast.success(draft ? 'Cover letter saved.' : 'Cover letter cleared.');
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function loadTemplate() {
    const tmpl =
      'Thank you for the opportunity to assess your facility. This report ' +
      'summarizes the findings from our recent walkthrough and documentation ' +
      'review. I have organized the items in order of priority so your team ' +
      'can address the highest-exposure issues first.\n\n' +
      'Please reach out directly with any questions or to schedule a follow-up. ' +
      'I look forward to continuing to support your safety program.';
    setDraft(tmpl);
  }

  return (
    <div
      className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200"
      data-testid="cover-letter-editor"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.18em] text-[#C9A84C] uppercase mb-0.5">
            Personalized Cover Letter
          </div>
          <div className="text-[13px] text-neutral-500">
            Renders as page 2 of the PDF. Leave blank to omit.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!draft && (
            <button
              type="button"
              onClick={loadTemplate}
              className="text-[11px] font-bold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200"
              style={{ fontFamily: 'Inter' }}
              data-testid="cover-letter-template"
            >
              Use Template
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || busy}
            className="text-[11px] font-bold uppercase tracking-[0.08em] px-4 py-1.5 rounded-full transition-all disabled:opacity-40 active:scale-[0.98]"
            style={{
              background: dirty ? '#142654' : '#F3F4F6',
              color: dirty ? 'white' : '#6B7280',
              fontFamily: 'Inter',
            }}
            data-testid="cover-letter-save"
          >
            {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={6}
        className="gl-input gl-textarea"
        placeholder="Address your client directly. e.g., 'Thank you for the opportunity to assess your facility…'"
        data-testid="cover-letter-textarea"
      />
      {dirty && (
        <div className="mt-2 text-[11px] text-neutral-500">
          Save and regenerate the report to include this in the PDF.
        </div>
      )}
    </div>
  );
}

// GL-SPEC-APP-025 — PDF Attachments uploader.
// Merge mode: appended to the report PDF on next generation.
// Attach mode: sent as a separate email file when delivering (future).
function AttachmentsPanel({ report, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('merge');
  const fileRef = useRef(null);
  const attachments = report.attachments || [];

  async function upload(file) {
    if (!file) return;
    const name = file.name.toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    const mergeExts = ['pdf'];
    const attachExts = ['pdf', 'xlsx', 'xls', 'csv', 'docx', 'doc', 'txt', 'png', 'jpg', 'jpeg'];
    const allowed = mode === 'merge' ? mergeExts : attachExts;
    if (!allowed.includes(ext)) {
      const kindMsg = mode === 'merge' ? 'PDF only (merge mode)' : 'PDF, Excel, Word, CSV, or image';
      toast.error(`File type not allowed in ${mode} mode. Accepted: ${kindMsg}.`);
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Max 20 MB.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('delivery_mode', mode);
      await api.post(`/reports/${report.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Attached "${file.name}".`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove(att) {
    if (!window.confirm(`Remove "${att.filename}" from this report?`)) return;
    try {
      await api.delete(`/reports/${report.id}/attachments/${att.id}`);
      toast.success('Attachment removed.');
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div
      className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200"
      data-testid="attachments-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.18em] text-[#C9A84C] uppercase mb-0.5">
            Document Attachments
          </div>
          <div className="text-[13px] text-neutral-500">
            Add quote sheets, signed proposals, Excel/CSV exports, or any
            supporting documents.
          </div>
        </div>
      </div>

      {/* Upload row */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="h-10 px-3 text-sm rounded-lg border border-neutral-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#2A52A0]/30"
          data-testid="attachment-mode"
        >
          <option value="merge">Merge into report PDF (PDF only)</option>
          <option value="attach">Email as separate file (PDF, Excel, Word, CSV)</option>
        </select>
        <input
          ref={fileRef}
          type="file"
          accept={
            mode === 'merge'
              ? 'application/pdf'
              : 'application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv,text/plain,image/png,image/jpeg'
          }
          onChange={(e) => upload(e.target.files?.[0])}
          disabled={busy}
          className="block flex-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-[0.08em] file:bg-[#142654] file:text-white hover:file:bg-[#2a3e3e] file:cursor-pointer cursor-pointer"
          data-testid="attachment-file-input"
        />
      </div>

      {/* Attachments list */}
      {attachments.length === 0 ? (
        <div className="text-[12.5px] text-neutral-400 italic">No attachments yet.</div>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200"
            >
              <FileTextIcon size={18} weight="bold" className="text-neutral-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-neutral-800 truncate">{a.filename}</div>
                <div className="text-[11px] text-neutral-500" style={{ fontFamily: 'Inter' }}>
                  {a.delivery_mode === 'merge' ? 'Merged into report' : 'Separate file'}
                  {a.size_bytes ? `  ·  ${Math.ceil(a.size_bytes / 1024)} KB` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(a)}
                className="p-2 rounded-full hover:bg-red-50 active:scale-95"
                style={{ color: '#991B1B' }}
                aria-label={`Remove ${a.filename}`}
                title="Remove"
                data-testid={`attachment-remove-${a.id}`}
              >
                <TrashIcon size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {attachments.some((a) => a.delivery_mode === 'merge') && (
        <div className="mt-3 text-[11.5px] text-neutral-500 italic">
          Re-generate the report to bake merged attachments into the next PDF.
        </div>
      )}
    </div>
  );
}

